import React, { useEffect, useState } from "react";
import {
  Box,
  PageLayout,
  Text,
  FormControl,
  TabNav,
  SelectPanel,
  Button,
  ButtonGroup,
} from "@primer/react";
import {
  SyncIcon,
  SearchIcon,
  ListUnorderedIcon,
  PersonIcon,
} from "@primer/octicons-react";
import { useSelector } from "react-redux";
import { useCommonContext } from "../../../contexts/common";
import { NotifyHelper } from "../../../helpers/toast";
import { phongThiApi } from "../../../api/phongThiApi";
import MyButton from "../../../components/ui/button/Button";
import DataTable from "../../../components/ui/data-table";
import DanhSachThiSinh from "./ds-thi-sinh";
import GiaoVienCoiThi from "./ds-giaovien-coithi";
import GiaoVienChamThi from "./ds-giaovien-chamthi";
import PhongThiTreeSelection from "./phongthi-tree-selection/PhongThiTreeSelection";
import { ComboboxTruong } from "../../../components/combobox-truong";
import ComboboxKyThi from "../../../components/combobox-kythi";
import { useCommonSelectedHook } from "../../../hooks/useCommonSelectedHook";

interface PhongThi {
  ex_phongthi_id: number;
  ex_kythi_monthi_id: number;
}

interface Filter {
  dm_truong_id: number;
  nam_hoc: string;
  ex_kythi_id: number;
  ex_kythi_monthi_id: number;
}

const PhongThiPage: React.FC = () => {
  const [selectedPhongThi, setSelectedPhongThi] = useState<PhongThi | null>(
    null
  );
  const [kyThiKetThuc, setKyThiKetThuc] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dsPhongHocs, setDSPhongHocs] = useState([]);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const { nam_hoc, dm_coso_id } = useCommonSelectedHook();
  const { translate } = useCommonContext();
  const [filter, setFilter] = useState<Filter>({
    dm_truong_id: 0,
    nam_hoc: nam_hoc,
    ex_kythi_id: 0,
    ex_kythi_monthi_id: 0,
  });

  const loadPhongThi = async () => {
    try {
      const res = await phongThiApi.selectTreeView(filter.ex_kythi_id || 0);
      if (res.is_success) {
        setDSPhongHocs(res.data);
      } else {
        setDSPhongHocs([]);
      }
      setSelectedPhongThi(null);
    } catch (error) {
      NotifyHelper.Error("Có lỗi khi tải dữ liệu phòng thi");
    }
  };

  useEffect(() => {
    loadPhongThi();
  }, [filter.ex_kythi_id]);

  const handleTruongChange = (value: number) => {
    setFilter((prev) => ({ ...prev, dm_truong_id: value || 0 }));
  };

  const handleKyThiChange = (value: number, data: any) => {
    setFilter((prev) => ({ ...prev, ex_kythi_id: value || 0 }));
    setKyThiKetThuc(data.da_ket_thuc);
  };

  const tabs = [
    {
      id: 0,
      name: "Danh sách thí sinh",
      icon: ListUnorderedIcon,
      style: { cursor: "pointer" },
    },
    {
      id: 1,
      name: "Giáo viên coi thi",
      icon: PersonIcon,
      style: { cursor: "pointer" },
    },
    {
      id: 2,
      name: "Giáo viên chấm thi",
      icon: PersonIcon,
      style: { cursor: "pointer" },
    },
  ];

  return (
    <Box sx={{ bg: "canvas.subtle", p: 3, height: "calc(100% - 240px)" }}>
      <Box
        sx={{
          bg: "canvas.default",
          borderRadius: 2,
          borderColor: "border.default",
          borderStyle: "solid",
          borderWidth: 1,
          display: "flex",
        }}
      >
        {/* Sidebar */}
        <Box
          sx={{
            borderRightWidth: 1,
            borderRightStyle: "solid",
            borderRightColor: "border.default",
            width: isNavCollapsed ? 48 : 256,
            transition: "width 0.2s",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottomWidth: 1,
              borderBottomStyle: "solid",
              borderBottomColor: "border.default",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {!isNavCollapsed && (
              <Text sx={{ fontWeight: "bold", fontSize:'22px' }}>Phòng thi</Text>
            )}
            <Button variant="invisible">
              {/* <ListUnorderedIcon /> */}
            </Button>
          </Box>
          <Box sx={{ p: 2, overflowY: "auto", flex: 1 }}>
            <PhongThiTreeSelection
              dataSource={dsPhongHocs}
              onSelectionChanged={setSelectedPhongThi}
            />
          </Box>
        </Box>

        {/* Main Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Filters */}
          <Box
            sx={{
              p: 2,
              borderBottomWidth: 1,
              borderBottomStyle: "solid",
              borderBottomColor: "border.default",
              bg: "canvas.subtle",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <ComboboxTruong
                value={filter.dm_truong_id}
                onValueChanged={handleTruongChange}
              />
              <ComboboxKyThi
                value={filter.ex_kythi_id}
                dm_truong_id={filter.dm_truong_id}
                onValueChanged={handleKyThiChange}
              />
              {/* <MyButton
                text="Làm mới"
                leadingVisual={SyncIcon}
                onClick={loadPhongThi}
              /> */}
            </Box>
          </Box>

          {/* Tabs */}
          <TabNav sx={{ px: 2, pt: 2 }}>
            {tabs.map((tab) => (
              <TabNav.Link
                key={tab.id}
                selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                sx={{ px: 3, ...tab.style }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  {tab.icon && <tab.icon size={16} />}
                  {translate(tab.name)}
                </Box>{" "}
              </TabNav.Link>
            ))}
          </TabNav>

          {/* Tab Content */}
          <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
            {activeTab === 0 && (
              <DanhSachThiSinh
                kyThiKetThuc={kyThiKetThuc}
                dm_truong_id={filter.dm_truong_id}
                ex_kythi_id={filter.ex_kythi_id}
                ex_phongthi_id={selectedPhongThi?.ex_phongthi_id || 0}
                ex_kythi_monthi_id={selectedPhongThi?.ex_kythi_monthi_id || 0}
              />
            )}
            {activeTab === 1 && (
              <GiaoVienCoiThi
                kyThiKetThuc={kyThiKetThuc}
                dm_truong_id={filter.dm_truong_id}
                ex_phongthi_id={selectedPhongThi?.ex_phongthi_id || 0}
                ex_kythi_monthi_id={selectedPhongThi?.ex_kythi_monthi_id || 0}
              />
            )}
            {activeTab === 2 && (
              <GiaoVienChamThi
                kyThiKetThuc={kyThiKetThuc}
                dm_truong_id={filter.dm_truong_id}
                ex_kythi_id={filter.ex_kythi_id}
                ex_phongthi_id={selectedPhongThi?.ex_phongthi_id || 0}
                ex_kythi_monthi_id={selectedPhongThi?.ex_kythi_monthi_id || 0}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PhongThiPage;
